// src/components/Booklet/PageTransitions.js (Minimal fix - preserving your working flips)
import React, { useEffect, useRef, useState, useCallback } from 'react';
import './PageTransitions.css';

const PageTransitions = ({ 
  transition, 
  currentPage, 
  zoom = 1, 
  interactions = [], 
  onInteractionClick
}) => {
  const pageRef = useRef(null);
  const containerRef = useRef(null);
  const [pageStyle, setPageStyle] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPageData, setCurrentPageData] = useState(null);
  const [previousPageData, setPreviousPageData] = useState(null);
  const [previousPageKey, setPreviousPageKey] = useState(null);
  const [phase, setPhase] = useState('idle');

  // Track page changes for transitions
  const currentPageKey = currentPage ? `${currentPage.pageNumber || 0}-${currentPage.url}` : null;

  useEffect(() => {
    if (currentPage) {
      // Safe dimension calculation with fallbacks
      const pageWidth = currentPage.dimensions?.width || currentPage.width || 600;
      const pageHeight = currentPage.dimensions?.height || currentPage.height || 800;

      // Ensure we have valid numbers
      const safeWidth = Number.isFinite(pageWidth) ? pageWidth : 600;
      const safeHeight = Number.isFinite(pageHeight) ? pageHeight : 800;
      const safeZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
      
      // Calculate container constraints
      const maxWidth = Math.min(window.innerWidth * 0.85, 1200);
      const maxHeight = Math.min(window.innerHeight * 0.75, 900);
      
      // Calculate aspect ratio safely
      const aspectRatio = safeWidth / safeHeight;
      const safeAspectRatio = Number.isFinite(aspectRatio) ? aspectRatio : 0.75;
      
      // Calculate display dimensions
      let displayWidth, displayHeight;
      
      if (maxWidth / safeAspectRatio <= maxHeight) {
        displayWidth = maxWidth;
        displayHeight = maxWidth / safeAspectRatio;
      } else {
        displayHeight = maxHeight;
        displayWidth = maxHeight * safeAspectRatio;
      }
      
      // Apply zoom safely
      const finalWidth = displayWidth * safeZoom;
      const finalHeight = displayHeight * safeZoom;
      
      // Ensure final values are valid
      setPageStyle({
        width: Number.isFinite(finalWidth) ? `${finalWidth}px` : '600px',
        height: Number.isFinite(finalHeight) ? `${finalHeight}px` : '800px',
        maxWidth: '100%',
        maxHeight: '100%'
      });
    }
  }, [currentPage, zoom]);

  // Handle page transitions with proper timing
  useEffect(() => {
    if (currentPageKey && previousPageKey && currentPageKey !== previousPageKey) {
      // 1) stage the old page
      setPreviousPageData(currentPageData);
      // 2) prepare for first half of flip
      setPhase('first');
      setIsTransitioning(true);

      return;
    }
    // initial load:
    if (currentPage && !currentPageData) {
      setCurrentPageData(currentPage);
    }
    setPreviousPageKey(currentPageKey);

  }, [currentPageKey, previousPageKey, currentPage]);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const onAnimEnd = (e) => {
      // only react to our first‑half animation
      if (phase === 'first' && e.animationName === 'realPageFlipFirst') {
        // swap in the new page content
        setCurrentPageData(currentPage);
        // clear out the old data (or keep if you want a fade overlap)
        setPreviousPageData(null);
        // now flip the "new" page into view
        setPhase('second');
      }
      // when second flip ends, wrap up
      else if (phase === 'second' && e.animationName === 'realPageFlipSecond') {
        setIsTransitioning(false);
        setPhase('idle');
      }
    };

    el.addEventListener('animationend', onAnimEnd);
    return () => el.removeEventListener('animationend', onAnimEnd);
  }, [phase]);

  const getTransitionDuration = useCallback((transitionType) => {
    switch (transitionType) {
      case 'flip': return 800;
      case 'curl': return 1000;
      case 'slide': return 600;
      case 'fade': return 500;
      case 'fold': return 900;
      default: return 500;
    }
  }, []);

  const getTransitionClasses = () => {
    const baseClass = 'page-transition';
    const typeClass = `page-${transition || 'fade'}`;
    const transitioningClass = isTransitioning ? 'transitioning' : '';
    return `${baseClass} ${typeClass} ${transitioningClass}`.trim();
  };

  const renderInteractions = () => {
    if (!Array.isArray(interactions) || isTransitioning) return null;
    
    return interactions.map((interaction) => {
      if (!interaction || !interaction.id) return null;
      
      // Safe position and size calculations
      const x = Number.isFinite(interaction.x) ? Math.max(0, Math.min(95, interaction.x)) : 0;
      const y = Number.isFinite(interaction.y) ? Math.max(0, Math.min(95, interaction.y)) : 0;
      const width = Number.isFinite(interaction.width) ? Math.max(2, Math.min(50, interaction.width)) : 10;
      const height = Number.isFinite(interaction.height) ? Math.max(2, Math.min(50, interaction.height)) : 10;
      
      return (
        <div
          key={interaction.id}
          className="absolute cursor-pointer transition-all duration-200 hover:scale-110 hover:z-30"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            width: `${width}%`,
            height: `${height}%`,
            zIndex: 20
          }}
          onClick={(e) => {
            e.stopPropagation();
            onInteractionClick && onInteractionClick(interaction);
          }}
        >
          {renderInteractionContent(interaction)}
        </div>
      );
    }).filter(Boolean);
  };

  const renderInteractionContent = (interaction) => {
    const baseClasses = "w-full h-full rounded-lg transition-all duration-200 hover:shadow-xl border-2 border-dashed flex items-center justify-center";
    
    switch (interaction.type) {
      case 'link':
        return (
          <div className={`${baseClasses} bg-blue-500 bg-opacity-10 border-blue-500 hover:bg-opacity-25`}>
            <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium shadow-lg">
              {interaction.data?.text || 'Link'}
            </div>
          </div>
        );
      
      case 'video':
        return (
          <div className={`${baseClasses} bg-red-500 bg-opacity-10 border-red-500 hover:bg-opacity-25`}>
            <div className="bg-red-600 text-white p-2 rounded-full shadow-lg">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        );
      
      case 'audio':
        return (
          <div className={`${baseClasses} bg-green-500 bg-opacity-10 border-green-500 hover:bg-opacity-25`}>
            <div className="bg-green-600 text-white p-2 rounded-full shadow-lg">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.796L4.828 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.828l3.555-2.796A1 1 0 019.383 3.076zM12 6.658a1 1 0 011.414 0c.914.914 1.414 2.156 1.414 3.414s-.5 2.5-1.414 3.414a1 1 0 01-1.414-1.414c.586-.586.914-1.414.914-2s-.328-1.414-.914-2A1 1 0 0112 6.658z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        );
      
      case 'popup':
        return (
          <div className={`${baseClasses} bg-purple-500 bg-opacity-10 border-purple-500 hover:bg-opacity-25`}>
            <div className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-medium shadow-lg">
              {interaction.data?.title || 'Info'}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!currentPageData) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading page...</p>
        </div>
      </div>
    );
  }

  return (
  <div ref={containerRef} className="page-transition-container">
    {/* FIXED: Use CSS Grid to overlay pages in same cell */}
    <div className="page-overlay-grid" style={pageStyle}>
      
      {/* Only outgoing page during first half */}
      {phase === 'first' && previousPageData && (
        <div className="page-transition page-flip-first transitioning page-overlay-item">
          <div className="page-content">
            <img 
              src={previousPageData.url} 
              alt={`Page ${previousPageData.pageNumber || 1}`}
              className="w-full h-full object-contain pointer-events-none select-none"
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Incoming (or static) page */}
      {currentPageData && (
        <div
          ref={pageRef}
          className={`page-transition page-overlay-item ${
            phase === 'first'
              ? 'page-flip-first transitioning'
              : phase === 'second'
              ? 'page-flip-second transitioning'
              : 'page-flip'
          }`}
        >
          <div className="page-content">
            <img 
              src={currentPageData.url} 
              alt={`Page ${currentPageData.pageNumber || 1}`}
              className="w-full h-full object-contain pointer-events-none select-none"
              draggable={false}
            />
            {renderInteractions()}
          </div>
        </div>
      )}
      
    </div>
  </div>
 );
};

export default PageTransitions;